import { alpha } from '@mui/material';
import { createTheme } from '@mui/material/styles';

const palette = {
  primary: {
    main: '#0578d6',
    secondary: '#00C6DF',
    dark: '#0A369D',
    accent1: '#FB6A00',
    accent2: '#FBE7C6',
    primarylight1: '#CCE5FF',
    primarylight2: '#AAD4FF',
    primarylight3: '#D9E7F1',
    primary100: '#E3F1FF',
    primary200: '#BADBFF',
    primary300: '#84BFFF',
    primary400: '#5C9EFF',
    primary500: '#3385FF',
    primary600: '#006BFF',
    primary750: '#1565c0',
    primary700: '#0058CC',
    primary800: '#004699',
    primary900: '#003266',
    neutral100: '#E5ECF1',
    neutral200: '#D8DEE3',
    neutral300: '#CACFD4',
    neutral400: '#AAAEB2',
    neutral500: '#56595a',
    neutral550: '#797D80',
    neutral600: '#545759',
    neutral700: '#3C3D3F',
    neutral800: '#343637',
    neutral900: '#242526',
    neutral1000: '#19191A',
    star: '#FFB33E',
    danger: '#DD1E31',
    danger90: '#f9d2d6',
    danger95: '#fce9ea',
    danger97: '#fdf2f3',
    success: '#25B86A',
    success90: '#d4f7e4',
    success95: '#e9fbf1',
    success97: '#f3fcf6',
    white: '#FFFFFF',
  },
  text: {
    primary: '#343637',
    secondary: '#56595a',
    disabled: '#AAAEB2',
    white: '#FFFFFF',
    dark: '#242526',
  },
  success: {
    main: '#25B86A',
    dark: '#0e995d',
  },
};

const shape = {
  defaultBorderRadius: '8px',
  secondaryBorderRadius: '6px',
  roundedButtonBorderRadius: '12px',
  checkboxBorderRadius: '3px',
  chipBorderRadius: '4px',
};

export const theme = createTheme({
  palette: {
    ...palette,
  },
  shape,
  typography: {
    fontFamily: `"Poppins", "Arial", sans-serif`,
    button: {
      textTransform: 'none',
      fontSize: '16px',
      borderRadius: shape.defaultBorderRadius,
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: shape.defaultBorderRadius,
          '&:hover': {
            backgroundColor: palette.primary.primary750,
            borderColor: palette.primary.primary750,
          },
          '&.Mui-disabled': {
            backgroundColor: palette.neutral100,
            color: palette.neutral400,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: shape.chipBorderRadius,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          marginTop: 8,
          borderRadius: shape.defaultBorderRadius,
          boxShadow: `0px 0px 32px ${palette.primary.neutral100},0 0px 10px ${palette.primary.neutral300}`,
          border: '1px solid',
          borderColor: palette.primary.neutral200,
          '& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected': {
            backgroundColor: palette.primary.neutral100,
          },
          '& .Mui-selected:hover': {
            backgroundColor: palette.primary.neutral100,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: '1px solid',
          borderColor: palette.primary.neutral200,
          borderRadius: shape.defaultBorderRadius,
          boxShadow: `0px 0px 32px rgba(0, 0, 0, 0.15),0 0px 10px rgba(0, 0, 0, 0.1)`,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          '>:not(style)~:not(style)': {
            marginLeft: 0,
          },
        },
      },
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(palette.primary.neutral100, 0.7),
          color: palette.primary.neutral550,
          textTransform: 'uppercase',
          borderBottom: 'none',
          paddingTop: '8px',
          paddingBottom: '8px',
          borderRadius: '0px',
          '&:last-child': {
            borderTopRightRadius: shape.defaultBorderRadius,
            borderBottomRightRadius: shape.defaultBorderRadius,
          },
          '&:first-of-type': {
            borderTopLeftRadius: shape.defaultBorderRadius,
            borderBottomLeftRadius: shape.defaultBorderRadius,
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(palette.primary.neutral100, 0.7),
          color: palette.primary.neutral550,
          textTransform: 'uppercase',
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  },
  transitions: {
    duration: {
      standard: '0.4s',
    },
  },
});
